import { doc, updateDoc, deleteDoc, getDoc, writeBatch, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { AllUser } from '../hooks/useAllUsers';
import { SuperAdminInvitationService } from './invitations';
import { UserRole, SuperAdminRole, MunicipalityAssignment, MunicipalityAccessLevel, MunicipalityPermissions, getAccessLevelLabel } from '@municipality/types';
import { NotificationService } from './notifications';

export class SuperAdminUserService {
  // Update user information
  static async updateUser(userId: string, updates: Partial<AllUser> & {
    name?: string;
    surname?: string;
    phoneNumber?: string;
    superAdminRole?: SuperAdminRole;
  }): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete user (and any pending invitations for that email)
  static async deleteUser(userId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      const userEmail = userSnap.exists() ? (userSnap.data()?.email as string | undefined) : undefined;

      await deleteDoc(userRef);

      if (userEmail) {
        try {
          await SuperAdminInvitationService.deleteInvitationsByEmail(userEmail);
        } catch (invErr) {
          console.warn('Failed to delete invitations for user email (non-blocking):', invErr);
        }
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Get user by ID
  static async getUserById(userId: string): Promise<AllUser | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return {
          ...userSnap.data() as AllUser,
          id: userSnap.id
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  // Update user role
  static async updateUserRole(userId: string, role: UserRole): Promise<void> {
    return this.updateUser(userId, { role });
  }

  // Update user status
  static async updateUserStatus(userId: string, status: 'active' | 'inactive' | 'pending'): Promise<void> {
    return this.updateUser(userId, { status });
  }

  // Update user municipality
  static async updateUserMunicipality(userId: string, municipalityId: string): Promise<void> {
    return this.updateUser(userId, { municipalityId } as Partial<AllUser>);
  }

  /**
   * Assign multiple municipalities to a user in bulk using Firestore batch writes
   */
  static async assignMunicipalities(
    userId: string,
    municipalityIds: string[],
    accessLevel: MunicipalityAccessLevel,
    permissions: MunicipalityPermissions,
    grantedBy: string,
    options?: {
      expiresAt?: Date;
      notes?: string;
      grantedByName?: string;
      userName?: string;
      userEmail?: string;
    }
  ): Promise<void> {
    try {
      const validIds = municipalityIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
      if (validIds.length === 0) {
        throw new Error('No valid municipality IDs provided');
      }

      const userRef = doc(db, 'users', userId);
      const batch = writeBatch(db);

      // Create assignments for each municipality
      const assignments: MunicipalityAssignment[] = validIds.map(municipalityId => ({
        municipalityId,
        accessLevel,
        permissions,
        grantedBy,
        grantedAt: new Date(),
        expiresAt: options?.expiresAt,
        notes: options?.notes,
      }));

      // Build all firestore assignments - single arrayUnion for entire batch (last-write wins per field)
      const firestoreAssignments: Record<string, unknown>[] = [];
      for (const assignment of assignments) {
        const firestoreAssignment: Record<string, unknown> = {
          municipalityId: assignment.municipalityId,
          accessLevel: assignment.accessLevel ?? MunicipalityAccessLevel.READ_ONLY,
          grantedBy: assignment.grantedBy ?? '',
          grantedAt: Timestamp.now(),
        };
        if (assignment.expiresAt) {
          firestoreAssignment.expiresAt = Timestamp.fromDate(assignment.expiresAt);
        }
        if (assignment.notes != null && assignment.notes !== '') {
          firestoreAssignment.notes = assignment.notes;
        }
        const perms = (assignment.permissions ?? {}) as unknown as Record<string, unknown>;
        firestoreAssignment.permissions = Object.fromEntries(
          Object.entries(perms).map(([k, v]) => [k, v === undefined ? false : v])
        ) as unknown as MunicipalityPermissions;
        firestoreAssignments.push(firestoreAssignment);
      }

      batch.update(userRef, {
        municipalityAssignments: arrayUnion(...firestoreAssignments),
        assignedMunicipalityIds: arrayUnion(...validIds),
        updatedAt: new Date().toISOString()
      });

      await batch.commit();

      // Send notifications for each assigned municipality
      if (options?.userName && options?.userEmail) {
        for (const municipalityId of validIds) {
          // Fetch municipality details for notification
          const municipalityRef = doc(db, 'municipalities', municipalityId);
          const municipalitySnap = await getDoc(municipalityRef);
          const municipalityData = municipalitySnap.exists() ? municipalitySnap.data() : null;

          if (municipalityData) {
            await NotificationService.sendAssignmentNotification({
              userId,
              userName: options.userName,
              userEmail: options.userEmail,
              municipalityName: municipalityData.name || 'Unknown',
              municipalityCountry: municipalityData.country || 'Unknown',
              accessLevel: getAccessLevelLabel(accessLevel),
              grantedBy: options.grantedByName || grantedBy,
              grantedAt: new Date().toISOString(),
              expiresAt: options.expiresAt?.toISOString(),
              notes: options.notes,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error assigning municipalities:', error);
      throw error;
    }
  }

  /**
   * Revoke access to a specific municipality
   */
  static async revokeMunicipalityAccess(
    userId: string,
    municipalityId: string,
    options?: {
      revokedBy?: string;
      userName?: string;
      userEmail?: string;
      municipalityName?: string;
    }
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userSnap.data();
      const currentAssignments = (userData.municipalityAssignments || []) as MunicipalityAssignment[];
      
      // Filter out the assignment for this municipality, sanitize remaining for Firestore
      const updatedAssignments = currentAssignments
        .filter((assignment: MunicipalityAssignment) => assignment.municipalityId !== municipalityId)
        .map((a) => this.sanitizeAssignmentForFirestore(a as MunicipalityAssignment & { municipalityName?: string; municipalityCountry?: string }));

      await updateDoc(userRef, {
        municipalityAssignments: updatedAssignments,
        // Remove from denormalized flat array for Firestore security rules
        assignedMunicipalityIds: arrayRemove(municipalityId),
        updatedAt: new Date().toISOString()
      });

      // Send revocation notification
      if (options?.userName && options?.userEmail && options?.municipalityName && options?.revokedBy) {
        await NotificationService.sendAccessRevokedNotification(
          options.userEmail,
          options.userName,
          options.municipalityName,
          options.revokedBy
        );
      }
    } catch (error) {
      console.error('Error revoking municipality access:', error);
      throw error;
    }
  }

  /**
   * Sanitize permissions so Firestore receives no undefined values
   */
  private static sanitizePermissions(perms: MunicipalityPermissions): MunicipalityPermissions {
    const p = (perms ?? {}) as unknown as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(p).map(([k, v]) => [k, v === undefined ? false : v])
    ) as unknown as MunicipalityPermissions;
  }

  /**
   * Build Firestore-safe assignment object (no undefined values)
   */
  private static sanitizeAssignmentForFirestore(a: MunicipalityAssignment & { municipalityName?: string; municipalityCountry?: string }): Record<string, unknown> {
    const perms = this.sanitizePermissions(a.permissions ?? {} as MunicipalityPermissions);
    const out: Record<string, unknown> = {
      municipalityId: a.municipalityId,
      accessLevel: a.accessLevel ?? MunicipalityAccessLevel.READ_ONLY,
      permissions: perms,
      grantedBy: a.grantedBy ?? '',
      grantedAt: a.grantedAt instanceof Date ? Timestamp.fromDate(a.grantedAt) : a.grantedAt,
    };
    if (a.expiresAt != null) {
      out.expiresAt = a.expiresAt instanceof Date ? Timestamp.fromDate(a.expiresAt) : a.expiresAt;
    }
    if (a.notes != null && a.notes !== '') {
      out.notes = a.notes;
    }
    return out;
  }

  /**
   * Update permissions for an existing municipality assignment
   */
  static async updateMunicipalityPermissions(
    userId: string,
    municipalityId: string,
    updates: {
      accessLevel?: MunicipalityAccessLevel;
      permissions?: MunicipalityPermissions;
      expiresAt?: Date | null;
      notes?: string;
    }
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userSnap.data();
      const currentAssignments = (userData.municipalityAssignments || []) as MunicipalityAssignment[];
      
      // Find and update the specific assignment, then sanitize for Firestore
      const updatedAssignments = currentAssignments.map((assignment: MunicipalityAssignment) => {
        if (assignment.municipalityId !== municipalityId) {
          return this.sanitizeAssignmentForFirestore(assignment as MunicipalityAssignment & { municipalityName?: string; municipalityCountry?: string });
        }
        const merged: MunicipalityAssignment & { municipalityName?: string; municipalityCountry?: string } = {
          ...assignment,
          ...(updates.accessLevel != null && { accessLevel: updates.accessLevel }),
          ...(updates.permissions != null && { permissions: this.sanitizePermissions(updates.permissions) }),
          ...(updates.expiresAt !== undefined && {
            expiresAt: updates.expiresAt ?? null,
          }),
          ...(updates.notes !== undefined && { notes: updates.notes ?? null }),
        };
        // Omit expiresAt when null so we don't store undefined-like
        if (merged.expiresAt === null) {
          delete (merged as unknown as Record<string, unknown>).expiresAt;
        }
        return this.sanitizeAssignmentForFirestore(merged);
      });

      await updateDoc(userRef, {
        municipalityAssignments: updatedAssignments,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating municipality permissions:', error);
      throw error;
    }
  }

  /**
   * Renew/resend an expired assignment
   */
  static async resendExpiredAssignment(
    userId: string,
    municipalityId: string,
    newExpirationDate?: Date,
    options?: {
      renewedBy?: string;
      userName?: string;
      userEmail?: string;
      municipalityName?: string;
    }
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userSnap.data();
      const currentAssignments = (userData.municipalityAssignments || []) as MunicipalityAssignment[];
      
      // Find and renew the assignment, sanitize for Firestore
      // When newExpirationDate is undefined, clear expiresAt so the renewed assignment becomes valid
      const updatedAssignments = currentAssignments.map((assignment: MunicipalityAssignment) => {
        if (assignment.municipalityId === municipalityId) {
          const renewed = {
            ...assignment,
            grantedAt: new Date(),
            expiresAt: newExpirationDate !== undefined ? newExpirationDate ?? undefined : undefined,
          };
          return this.sanitizeAssignmentForFirestore(renewed as MunicipalityAssignment & { municipalityName?: string; municipalityCountry?: string });
        }
        return this.sanitizeAssignmentForFirestore(assignment as MunicipalityAssignment & { municipalityName?: string; municipalityCountry?: string });
      });

      await updateDoc(userRef, {
        municipalityAssignments: updatedAssignments,
        updatedAt: new Date().toISOString()
      });

      // Send renewal notification
      if (options?.userName && options?.userEmail && options?.municipalityName && options?.renewedBy) {
        await NotificationService.sendAssignmentRenewedNotification(
          options.userEmail,
          options.userName,
          options.municipalityName,
          options.renewedBy,
          newExpirationDate?.toISOString()
        );
      }
    } catch (error) {
      console.error('Error resending expired assignment:', error);
      throw error;
    }
  }
}
